/**
 * Implements a uniswap datafeed
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  ResolutionString,
  ErrorCallback,
  HistoryCallback,
  LibrarySymbolInfo,
  PeriodParams,
  SubscribeBarsCallback,
  IDatafeedChartApi,
  SearchSymbolsCallback,
  Bar,
  GetMarksCallback,
  Mark,
  TimescaleMark,
} from '../charting_library';
import {
  filterSymbolFromTestnet,
  hasTestnetSymbol,
  pairConvertable,
} from './helpers/symbols';
import { KattanaAPI, store, subscription } from '../../../store/createStore';
import eventEmitter from '../../../core/eventEmitter';
import { getLiveCandlesEventName } from '../../../helpers/events-builder';
import { enums, helpers, ITicker, IDexChartTrade } from '@kattana-io/common';
import normalizeCandles from './helpers/normalizeCandles';
import useTicker from '../../../hooks/useTicker';
import { enableLiquiditFilter } from '../../Modal/TickersModal/helpers';
import { capitalize } from '../../Modal/TickersModal/helpers';
import { decorateExchangeName } from '../../../helpers/decorators';
import bigInt from 'big-integer';
import { base64ToHex } from '../../Trading/helpers/elrondConverters';
import { chartPrecision } from '../../../helpers/truncate';
import { fetchMarks, fetchTimescaleMarks } from './helpers/fetchMarks';

dayjs.extend(utc);
dayjs.extend(timezone);

interface IDexSymbol {
  symbol: string; //WETH/USDT
  symbol_name: string; // WETHUSDT
  base_asset: string; // WETH
  quote_asset: string; // USDT
  base_address: string;
  quote_address: string;
  precision?: any;
}

interface IFindSymbolPayload {
  symbolId?: number;
  query?: string;
  address?: string;
  testnet?: boolean;
}

export interface ICandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface BackendCandle {
  Close: number;
  Date: number;
  High: number;
  Low: number;
  Open: number;
}

export interface BackendVolume {
  VolumeA: string;
  VolumeB: string;
  VolumeUSD: number;
}

export interface BackendCandlesObj {
  Candle: BackendCandle[];
  Volume: BackendVolume;
  empty: boolean;
}

/**
 * Route name used in backend
 */

interface IUniswapConstructorOptions {
  debug: boolean;
  usdConversion: boolean;
  supportedResolution: string[];
  tokenUnlocks?: boolean;
}
interface IChartSymbol {
  symbol: string;
  symbol_name?: string;
  base_asset: string;
  quote_asset: string;
  base_address: string;
  quote_address: string;
  address?: string;
  original_quote: string;
  precision: number;
  flipped: boolean;
  ticker: ITicker;
}
/* eslint-disable no-restricted-syntax */
class DexDatafeed implements IDatafeedChartApi {
  debug: boolean;
  symbols: IDexSymbol[];
  subscriptions: any;
  subscribedTicker?: ITicker;
  subscriptionsToEventMap: any;
  lastBar: any;
  lastBarMinuteTime: number;
  lastBarMinuteVolume: number;
  firstBar: any;
  onTick: any;
  timeframe: enums.EParsedTimeframes | undefined;
  usdConversion: boolean;
  onResetCacheNeededCallback: any;
  onLastPriceHandler: any;
  supportedResolution: string[];
  tokenUnlocks?: boolean;

  constructor(options: IUniswapConstructorOptions) {
    this.symbols = [];
    this.debug = options.debug || false;
    this.usdConversion = options.usdConversion;
    this.supportedResolution = options.supportedResolution;
    this.tokenUnlocks = options.tokenUnlocks;
    this.lastBar = {};
    this.lastBarMinuteTime = 0;
    this.lastBarMinuteVolume = 0;
    this.firstBar = {};

    this.subscriptions = {};

    this.subscriptionsToEventMap = {};
  }

  /**
   * Set flag and refetch candles
   */
  toggleUSDConversion = () => {
    this.usdConversion = !this.usdConversion;
  };

  /**
   * Request remote symbols
   */
  private findSymbols = async ({
    symbolId,
    query,
    address,
    testnet,
  }: IFindSymbolPayload): Promise<IChartSymbol[]> => {
    const tickers = await KattanaAPI.tickers({
      id: symbolId,
      searchRaw: query,
      sort: { name: 'liquidity', order: 'DESC' },
      address,
      kind: testnet ? undefined : enums.ETickerKind.DEX,
      testnet,
    });
    if (!tickers) {
      return [];
    }

    return tickers.map((item: ITicker) => {
      const { base, quote, name, base_address, quote_address, quote_decimals } =
        useTicker(item);

      return {
        symbol: name,
        symbol_name: item.address, // well, this is how we get input
        base_asset: base,
        quote_asset: quote,
        original_quote: quote,
        base_address: base_address,
        quote_address: quote_address,
        address: item.address,
        precision: quote_decimals,
        flipped: item.flipped,
        ticker: item,
      };
    });
  };

  buildCandle = (date: number, price: number): IDexChartTrade => ({
    date,
    volume: '0',
    close: price,
    high: price,
    low: price,
    open: price,
  });

  fetchKlines = async (
    symbol: LibrarySymbolInfo & any,
    interval: enums.ETimeframes,
    startTime: number,
    endTime: number,
    limit: number = 500,
    poolCreated: number
  ): Promise<IDexChartTrade[]> => {
    try {
      const ticker = symbol.data.ticker;
      this.timeframe = helpers.datafeedCommom.mapInterval(interval);

      const convertation = this.usdConversion
        ? symbol.data.original_quote
        : undefined;

      let liquidityFilter = 0;

      if (enableLiquiditFilter(ticker.chain || '')) {
        liquidityFilter = 1;
        const liquidity = ticker.liquidity;

        if (liquidity && liquidity > 0) {
          if (liquidity > 10_000_000) {
            liquidityFilter = 2;
          }
          if (liquidity > 100_000_000) {
            liquidityFilter = 3;
          }
        }
      }

      const data = await KattanaAPI.chart(
        symbol.chain,
        this.timeframe,
        symbol.data.address,
        startTime,
        endTime,
        limit,
        convertation,
        symbol.data.flipped,
        symbol.data.precision,
        poolCreated,
        liquidityFilter
      );

      return data || ([] as IDexChartTrade[]);
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  public isIntervalSupported = (interval: string): boolean =>
    this.supportedResolution.includes(interval);

  public onReady = async (callback: any) => {
    try {
      setTimeout(() => {
        callback({
          supports_marks: true,
          supports_timescale_marks: true,
          supports_time: true,
          supported_resolutions: this.supportedResolution,
        });
      }, 0);
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * TV method: when user types in search field
   * @param userInput
   * @param exchange
   * @param symbolType
   * @param onResultReadyCallback
   */
  public searchSymbols = async (
    userInput: string,
    exchange: string,
    symbolType: string,
    onResultReadyCallback: SearchSymbolsCallback
  ) => {
    const symbolId = -1;
    await this.findSymbols({ symbolId, query: userInput }).then(
      (symbols: any[]) => {
        onResultReadyCallback(
          symbols.map((symbol) => ({
            symbol: symbol.symbol,
            full_name: symbol.symbol,
            description: `${symbol.base_asset} / ${symbol.quote_asset}`,
            pair: `${symbol.quoteAsset}-${symbol.base_asset}`,
            ticker: symbol.symbol,
            exchange: capitalize(
              decorateExchangeName(symbol.ticker.exchange, symbol.ticker.chain)
            ),
            chain: symbol.chain,
            type: 'stock',
          }))
        );
      }
    );
  };

  /**
   * Creates a fake USD converted symbol if necessary
   * @param symbol
   * @param USDConverted
   * @returns
   */
  private symbolIterator = (symbol: IChartSymbol): LibrarySymbolInfo & any => {
    // const GENERIC_PRICESCALE = 10 ** 8; // USD precision
    const { quote_address, price, price_usd } = useTicker(symbol.ticker);
    const exchange = capitalize(
      decorateExchangeName(symbol.ticker.exchange, symbol.ticker.chain)
    );

    if (pairConvertable(symbol.ticker.chain, quote_address)) {
      if (this.usdConversion) {
        const pricePrecision = chartPrecision(price_usd);
        return {
          name: symbol.symbol,
          description: `${symbol.base_asset} / USD (converted)`,
          pair: symbol.symbol,
          ticker: symbol.address,
          session: '24x7',
          minmov: 1,
          pricescale: 10 ** pricePrecision,
          timezone: 'Etc/UTC',
          has_seconds: true,
          has_intraday: true,
          has_daily: true,
          has_weekly_and_monthly: true,
          original_currency_code: symbol.quote_asset,
          currency_code: symbol.quote_asset,
          data: symbol,
          exchange,
          chain: symbol.ticker.chain,
        };
      }
    }

    const pricePrecision = chartPrecision(price);
    return {
      name: symbol.symbol,
      description: `${symbol.base_asset} / ${symbol.quote_asset}`,
      pair: `${symbol.base_asset}/${symbol.quote_asset}`,
      ticker: symbol.address,
      session: '24x7',
      minmov: 1,
      pricescale: 10 ** pricePrecision,
      timezone: 'UTC',
      has_seconds: true,
      has_intraday: true,
      has_daily: true,
      has_weekly_and_monthly: true,
      original_currency_code: symbol.quote_asset,
      currency_code: symbol.quote_asset,
      data: symbol,
      exchange,
      chain: symbol.ticker.chain,
    };
  };

  /**
   * TV method
   * Find symbol
   * @param symbol_name
   * @param onSymbolResolvedCallback
   * @param onResolveErrorCallback
   */
  public resolveSymbol(
    symbolName: string,
    onSymbolResolvedCallback: any,
    onResolveErrorCallback: any
  ) {
    if (this.debug) {
      console.log('👉 resolveSymbol:', symbolName);
    }

    const searchById = symbolName.includes('{id}:');
    const symbolComps = symbolName.split(':');
    let address = '';
    let symbolId = -1;

    if (!searchById) {
      address = filterSymbolFromTestnet(
        (symbolComps.length > 1 ? symbolComps[1] : symbolName).trim()
      );
    } else {
      symbolId = Number(symbolComps[1]);
    }

    const payload: IFindSymbolPayload = {
      symbolId,
      address,
      testnet: hasTestnetSymbol(symbolName),
    };

    this.findSymbols(payload).then((symbols: IChartSymbol[]) => {
      if (symbols?.length) {
        const [symbol] = symbols;
        onSymbolResolvedCallback(this.symbolIterator(symbol));
      } else {
        onResolveErrorCallback('Not found');
      }
    });
  }

  // Kattana trades adapter
  klineToCandle = (kline: IDexChartTrade): ICandle => ({
    time: Number(kline.date * 1000),
    open: parseFloat(kline.open.toString()),
    high: parseFloat(kline.high.toString()),
    low: parseFloat(kline.low.toString()),
    close: parseFloat(kline.close.toString()),
    volume: parseFloat(kline.volume),
  });

  getBars(
    symbol: LibrarySymbolInfo & any,
    resolution: any,
    periodParams: PeriodParams,
    onResult: HistoryCallback,
    onError: ErrorCallback
  ) {
    if (this.debug) {
      console.log('👉 getBars:', symbol.name, resolution);
      console.log('First:', periodParams.firstDataRequest);
      console.log(
        'From:',
        periodParams.from,
        new Date(periodParams.from * 1000).toUTCString()
      );
      console.log(
        'To:  ',
        periodParams.to,
        new Date(periodParams.to * 1000).toUTCString()
      );
    }

    if (!resolution) {
      onError('Invalid interval');
    }

    const ticker = symbol.data.ticker;
    let totalKlines: IDexChartTrade[] = [];

    const finishKlines = () => {
      if (this.debug) {
        console.log('📊:', totalKlines.length);
      }

      if (ticker) {
        if (totalKlines.length === 0) {
          if (periodParams.from > ticker.pool_created * 1000) {
            onResult([]);
          } else {
            onResult([], { noData: true });
          }
        } else {
          // Get last candle from first request (right to left)
          // Otherwise we will take invalid candle
          if (periodParams.firstDataRequest) {
            const lastBar = this.klineToCandle(
              totalKlines[totalKlines.length - 1]
            );
            this.lastBar[ticker.address] = lastBar;
          } else {
            // if it's not first request - we need to glue our parts of chart
            if (this.firstBar.close) {
              totalKlines[totalKlines.length - 1].close = this.firstBar.close;
            }
          }

          this.firstBar = totalKlines[0];
          onResult(normalizeCandles(totalKlines.map(this.klineToCandle)), {
            noData: false,
          });
        }
      }
    };

    // eslint-disable-next-line no-shadow
    const getKlines = (
      from: number,
      to: number,
      poolCreated: number,
      limit = 500
    ) => {
      this.fetchKlines(symbol, resolution, from, to, limit, poolCreated)
        .then((klines) => {
          // TODO: Remove filter once we fix backend
          totalKlines = totalKlines.concat(klines.filter((item) => !!item));
          finishKlines();
          return null;
        })
        .catch((err) => {
          console.error(err);
          onError('Some problem');
        });
    };

    // // eslint-disable-next-line no-param-reassign
    // periodParams.from *= 1000;
    // // eslint-disable-next-line no-param-reassign
    // periodParams.to *= 1000;
    const limit = periodParams.countBack ? periodParams.countBack : 500;

    getKlines(
      periodParams.from,
      periodParams.to,
      ticker.pool_created || 0,
      limit
    );
  }

  getMarks: (
    symbolInfo: LibrarySymbolInfo,
    from: number,
    to: number,
    onDataCallback: GetMarksCallback<Mark>,
    resolution: ResolutionString
  ) => void = (...args) => {
    const address = store.getState().dex_trades.tradesWallet;
    fetchMarks(...args, this.usdConversion, address);
  };

  getTimescaleMarks = (
    symbolInfo: LibrarySymbolInfo,
    from: number,
    to: number,
    onDataCallback: GetMarksCallback<TimescaleMark>,
    resolution: ResolutionString
  ) => {
    if (this.tokenUnlocks) {
      fetchTimescaleMarks(symbolInfo, from, to, onDataCallback, resolution);
    }
  };

  getSubscriptionName = (
    symInfo: LibrarySymbolInfo & any,
    subscriberUID: string
  ) => {
    const ticker = symInfo.data.ticker;
    const chain = ticker.chain;

    if (ticker) {
      const subscription = getLiveCandlesEventName(
        chain,
        symInfo.data.ticker.address
      );
      this.subscriptions[subscriberUID] = subscription;
      return subscription;
    }

    return '';
  };

  onLastPrice = ({
    pair,
    candle: obj,
  }: {
    pair: string;
    candle: BackendCandlesObj;
  }) => {
    const ticker = this.subscribedTicker;
    // we have a delay between subscribeBars AND unsubscribeBars
    // when we change pair from more liquid to less liquid, we can receive for a little time candles from previous pair
    // until TradingView core call unsubscribeBars function
    // to prevent this not correct behaviour we need such trick
    const candleFromThisTicker = ticker?.address == pair;

    if (ticker && this.timeframe && candleFromThisTicker) {
      if (obj.empty) {
        // build new empty candle
        const emptyCandleTime = Math.floor(Date.now() / 1000);
        const newTime = helpers.datafeedCommom.formatDateByTimeframe(
          emptyCandleTime,
          this.timeframe
        );
        const barMinuteTime = helpers.datafeedCommom.formatDateByTimeframe(
          emptyCandleTime,
          enums.EParsedTimeframes.minute
        );

        const emptyCandle: Bar = {
          open: this.lastBar[ticker.address].close,
          close: this.lastBar[ticker.address].close,
          high: this.lastBar[ticker.address].close,
          low: this.lastBar[ticker.address].close,
          volume: 0,
          time: newTime,
        };

        const isNewCandle = newTime > this.lastBar[ticker.address].time;
        if (isNewCandle) {
          this.onTick({ ...emptyCandle });
          this.lastBar[ticker.address] = emptyCandle;
          this.lastBarMinuteTime = barMinuteTime;
          this.lastBarMinuteVolume = 0;
        }

        return;
      }

      let candle: ICandle;
      const dTicker = useTicker(ticker);

      let vol = 0;
      if (ticker.flipped) {
        vol = obj.Volume.VolumeA
          ? bigInt(base64ToHex(obj.Volume.VolumeA), 16).toJSNumber()
          : 0;
      } else {
        vol = obj.Volume.VolumeA
          ? bigInt(base64ToHex(obj.Volume.VolumeB), 16).toJSNumber()
          : 0;
      }

      if (ticker.flipped) {
        if (this.usdConversion) {
          candle = {
            open: obj.Candle[3].Open, // candle_b_usd open
            close: obj.Candle[3].Close, // candle_b_usd close
            high: obj.Candle[3].High, // candle_b_usd high
            low: obj.Candle[3].Low, // candle_b_usd low
            time: obj.Candle[3].Date, // candle_b_usd date
            volume: obj.Volume.VolumeUSD || 0, // candle_volume_usd
          };
        } else {
          candle = {
            open: obj.Candle[1].Open, // candle_b open
            close: obj.Candle[1].Close, // candle_b close
            high: obj.Candle[1].High, // candle_b high
            low: obj.Candle[1].Low, // candle_b low
            time: obj.Candle[1].Date, // candle_b date
            volume: vol / 10 ** dTicker.quote_decimals,
          };
        }
      } else {
        // not flipped pair
        if (this.usdConversion) {
          candle = {
            open: obj.Candle[2].Open, // candle_a_usd open
            close: obj.Candle[2].Close, // candle_a_usd close
            high: obj.Candle[2].High, // candle_a_usd high
            low: obj.Candle[2].Low, // candle_a_usd low
            time: obj.Candle[2].Date, // candle_a_usd date
            volume: obj.Volume.VolumeUSD || 0, // candle_volume_usd
          };
        } else {
          candle = {
            open: obj.Candle[0].Open, // candle_a open
            close: obj.Candle[0].Close, // candle_a close
            high: obj.Candle[0].High, // candle_a high
            low: obj.Candle[0].Low, // candle_a low
            time: obj.Candle[0].Date, // candle_a date
            volume: vol / 10 ** dTicker.quote_decimals, // amount0 / 10 ** decimals
          };
        }
      }

      const barMinuteTime = helpers.datafeedCommom.formatDateByTimeframe(
        candle.time,
        enums.EParsedTimeframes.minute
      );
      const newTime = helpers.datafeedCommom.formatDateByTimeframe(
        candle.time,
        this.timeframe
      );

      if (this.lastBar[ticker.address]) {
        const isNewCandle = newTime > this.lastBar[ticker.address].time;

        let newLastBar;

        // create new last candle
        if (isNewCandle) {
          newLastBar = {
            close: candle.close,
            high: candle.high,
            low: candle.low,
            open: candle.open,
            volume: candle.volume,
          };
        } else {
          let currentVolume = this.lastBar[ticker.address].volume;
          const firstAfterLoad = this.lastBarMinuteTime == 0;

          if (!firstAfterLoad && barMinuteTime > this.lastBarMinuteTime) {
            // we get volume from new minute, just summ it
            currentVolume += candle.volume;
          } else {
            // we get volume from same minutes but other block
            const currentVolumeInMin = this.lastBarMinuteVolume;
            const newVolumeInMin = candle.volume;
            currentVolume += newVolumeInMin - currentVolumeInMin;
          }

          // update last candle
          newLastBar = {
            ...this.lastBar[ticker.address],
            high: Math.max(this.lastBar[ticker.address].high, candle.high),
            low: Math.min(this.lastBar[ticker.address].low, candle.low),
            close: candle.close,
            volume: currentVolume,
          };
        }

        newLastBar.time = newTime;
        if (newTime >= this.lastBar[ticker.address].time) {
          this.onTick({ ...newLastBar });
          this.lastBar[ticker.address] = newLastBar;
          this.lastBarMinuteTime = barMinuteTime;
          this.lastBarMinuteVolume = candle.volume;
        }
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  subscribeBars(
    symbolInfo: LibrarySymbolInfo & any,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    subscriberUID: string,
    onResetCacheNeededCallback: () => void
  ) {
    this.onResetCacheNeededCallback = onResetCacheNeededCallback;
    if (this.debug) {
      console.log('👉 subscriberUID:', subscriberUID);
      console.log('👉 symbolInfo:', symbolInfo);
    }
    this.onTick = onTick;
    const eventName = this.getSubscriptionName(symbolInfo, subscriberUID);
    this.subscriptionsToEventMap[subscriberUID] = eventName;

    const ticker = symbolInfo.data.ticker;
    this.subscribedTicker = ticker;
    /**
     * Live event for trades
     */
    eventEmitter.addListener(eventName, this.onLastPrice);
    subscription.subscribeToLiveCandles(eventName, ticker);
  }

  unsubscribeBars(subscriberUID: any) {
    if (this.debug) {
      console.log('👉 unsubscribeBars:', subscriberUID);
    }

    const eventName = this.subscriptionsToEventMap[subscriberUID];
    if (eventName) {
      subscription.unsubscribeFromLiveCandles(eventName);
      eventEmitter.removeListener(eventName, this.onLastPrice);

      delete this.subscriptionsToEventMap[subscriberUID];
    }
  }
}

export default DexDatafeed;
